import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import AnimatedBackground from '../components/AnimatedBackground';
import { images } from '../data/images';
import { ITThematicImages } from '../components/ITThematicImages';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Monitor, 
  Cloud, 
  Database, 
  Code2,
  CheckCircle2 
} from 'lucide-react';

const ServicesPage = () => {
  const { t } = useLanguage();

  const serviceIcons = {
    consulting: Monitor,
    cloud: Cloud,
    data: Database,
    software: Code2,
  };

  const serviceImages = {
    consulting: ITThematicImages.itConsulting,
    cloud: ITThematicImages.cloudInfrastructure,
    data: ITThematicImages.dataAnalytics,
    software: ITThematicImages.softwareDevelopment,
  };

  const serviceColors = {
    consulting: '#0066CC',
    cloud: '#2B3A4D',
    data: '#0066CC',
    software: '#2B3A4D',
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#1E2A38] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: `url(${images.patterns.grid})` }}
        />
        <AnimatedBackground className="opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1E2A38]/50 to-[#1E2A38]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('services.title')}
            </h1>
            <p className="text-white/70 text-lg max-w-2xl">
              {t('services.subtitle')}
            </p>
            <div className="w-24 h-1 bg-[#0066CC] rounded-full mt-8" />
          </ScrollReveal>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-16">
            {t('services.items').map((service, index) => {
              const IconComponent = serviceIcons[service.id];
              const accentColor = serviceColors[service.id];
              const serviceImage = serviceImages[service.id];
              const isReversed = index % 2 !== 0;
              
              return (
                <ScrollReveal key={service.id} delay={100}>
                  <Card 
                    className="bg-white border-0 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden group perspective-container card-3d"
                  >
                    <div className={`grid grid-cols-1 lg:grid-cols-12`}>
                      {/* Content Side */}
                      <div className={`lg:col-span-7 p-8 lg:p-12 ${isReversed ? 'lg:order-2' : ''}`}>
                        <CardHeader className="p-0 mb-6">
                          <div className="flex items-center gap-4 mb-4">
                            <div 
                              className="w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                              style={{ backgroundColor: `${accentColor}15` }}
                            >
                              <IconComponent className="w-7 h-7 transition-colors duration-300" style={{ color: accentColor }} />
                            </div>
                            <span className="text-[#6B7B8C] text-sm font-medium uppercase tracking-wider">
                              0{index + 1}
                            </span>
                          </div>
                          <CardTitle className="text-2xl md:text-3xl font-bold text-[#2B3A4D] group-hover:text-[#0066CC] transition-colors duration-300">
                            {service.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                          <p className="text-[#5C5C5C] text-lg mb-8 leading-relaxed">
                            {service.fullDesc}
                          </p>
                          <ul className="space-y-4">
                            {service.features.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-3 group/item">
                                <CheckCircle2 
                                  className="w-5 h-5 mt-0.5 flex-shrink-0 transition-transform duration-200 group-hover/item:scale-110" 
                                  style={{ color: accentColor }} 
                                />
                                <span className="text-[#5C5C5C] group-hover/item:text-[#2B3A4D] transition-colors duration-200">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </div>
                      
                      {/* Visual Side */}
                      <div 
                        className={`lg:col-span-5 relative min-h-[300px] lg:min-h-full overflow-hidden perspective-container ${isReversed ? 'lg:order-1' : ''}`}
                      >
                        {/* Background Image or SVG */}
                        {typeof serviceImage === 'object' ? (
                          <div className="absolute inset-0 w-full h-full transition-transform duration-700 group-hover:scale-110 group-hover:rotate-1 transform-3d">
                            {serviceImage}
                          </div>
                        ) : (
                          <img 
                            src={serviceImage}
                            alt={service.title}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            loading="lazy"
                          />
                        )}
                        
                        {/* Overlay */}
                        <div 
                          className="absolute inset-0 transition-opacity duration-300"
                          style={{ 
                            background: `linear-gradient(135deg, ${accentColor}90 0%, ${accentColor}40 100%)`,
                            mixBlendMode: 'multiply'
                          }}
                        />
                        
                        {/* Decorative Elements */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="relative">
                            {/* Concentric circles */}
                            <div 
                              className="w-32 h-32 rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border border-white/20 animate-pulse"
                              style={{ animationDuration: '3s' }}
                            />
                            <div 
                              className="w-48 h-48 rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border border-white/10"
                            />
                            <div 
                              className="w-64 h-64 rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border border-white/5"
                            />
                            
                            {/* Center icon */}
                            <div 
                              className="w-20 h-20 rounded-full flex items-center justify-center relative z-10 bg-white/20 backdrop-blur-sm group-hover:bg-white/30 transition-all duration-300 group-hover:scale-110"
                            >
                              <IconComponent className="w-10 h-10 text-white" />
                            </div>
                          </div>
                        </div>
                        
                        {/* Grid Pattern Overlay */}
                        <div className="absolute inset-0 opacity-20">
                          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                              <pattern id={`serviceGrid-${index}`} width="30" height="30" patternUnits="userSpaceOnUse">
                                <path d="M 30 0 L 0 0 0 30" fill="none" stroke="white" strokeWidth="0.5"/>
                              </pattern>
                            </defs>
                            <rect width="100%" height="100%" fill={`url(#serviceGrid-${index})`} />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </Card>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
